// Runs from the host realm on DB Manager's behalf, through the same grant
// check as every other host-side catalog read (see dbEngines.js).
import { invokeAsExtension as invoke } from '../../extensions/capabilities/enforce';
import { DB_QUERY_COMMANDS, onSchemaCacheInvalidated } from '../../extensions/dbEngines';
import { catalogFor } from './dbOverviewSections';
import { buildDiagramModel } from './dbDiagramModel';

/**
 * Catalog reads for the database overview: ONE statement per section,
 * information_schema / pg_catalog / sqlite_master — never a query per table,
 * so a schema with thousands of tables costs the same handful of round trips.
 *
 * Lazy and cached per (connection, scope): a section is read the first time it
 * is shown and kept until Refresh. Promises are cached (not results), so two
 * views asking at once share one query; a failure evicts itself so the next
 * look retries.
 */

const cache = new Map(); // key → { facts, counts, sections: Map, diagram }

/**
 * scope: the MySQL database, the Postgres schema (null = every user schema),
 * nothing on SQLite (one file, one 'main').
 */
export function overviewContext({ engine, extensionId, connId, dbName }) {
  const scope = engine === 'sqlite' ? '' : dbName || '';
  return { engine, extensionId, connId, scope };
}

function keyOf(ctx) {
  return `${ctx.extensionId}\u0000${ctx.connId}\u0000${ctx.engine}\u0000${ctx.scope}`;
}

function entryFor(ctx) {
  const key = keyOf(ctx);
  let e = cache.get(key);
  if (!e) {
    e = { facts: null, counts: null, sections: new Map(), diagram: null };
    cache.set(key, e);
  }
  return e;
}

/** Forget everything read for this context (the Refresh button). */
export function invalidateOverview(ctx) {
  cache.delete(keyOf(ctx));
}

/** Forget every context of a connection (it was refreshed or reconnected). */
export function invalidateOverviewConnection(extensionId, connId) {
  const prefix = `${extensionId}\u0000${connId}\u0000`;
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k);
}

export function bindParams(spec, scope) {
  return (spec.params || []).map((p) => (p === 'scope' ? scope : null));
}

/** Result rows as objects keyed by the statement's declared column names. */
export function rowsToRecords(rows, columns) {
  return (rows || []).map((row) => {
    const rec = {};
    columns.forEach((c, i) => {
      rec[c] = row[i] === undefined ? null : row[i];
    });
    return rec;
  });
}

async function runSpec(ctx, spec) {
  const res = await invoke(DB_QUERY_COMMANDS[ctx.engine], {
    extensionId: ctx.extensionId,
    connId: ctx.connId,
    sql: spec.sql,
    params: bindParams(spec, ctx.scope),
  });
  return rowsToRecords(res?.rows, spec.columns);
}

function cached(holder, field, load) {
  if (!holder[field]) {
    holder[field] = load().catch((err) => {
      holder[field] = null;
      throw err;
    });
  }
  return holder[field];
}

/** Header facts: one record (name, size, charset/encoding, owner, version…). */
export function loadFacts(ctx) {
  const spec = catalogFor(ctx.engine)?.facts;
  if (!spec) return Promise.resolve(null);
  return cached(entryFor(ctx), 'facts', async () => (await runSpec(ctx, spec))[0] || null);
}

/**
 * Object counts per section, one statement. An engine or a user that cannot
 * read one of the catalogs costs the counts, never the overview — the list
 * still loads per section, so null here just means "no number beside it".
 */
export function loadCounts(ctx) {
  const spec = catalogFor(ctx.engine)?.counts;
  if (!spec) return Promise.resolve({});
  const e = entryFor(ctx);
  if (!e.counts) {
    e.counts = runSpec(ctx, spec)
      .then((recs) => recs[0] || {})
      .catch(() => {
        e.counts = null;
        return {};
      });
  }
  return e.counts;
}

/**
 * Merge optional side queries (SQLite's row estimates from sqlite_stat1, sizes
 * from dbstat) into the main records by name. Each may be unavailable — no
 * ANALYZE yet, a build without DBSTAT — and then its columns stay empty.
 */
async function withExtras(ctx, records, extras) {
  if (!extras?.length) return records;
  const byName = new Map(records.map((r) => [r.name, r]));
  await Promise.all(
    extras.map((x) =>
      runSpec(ctx, x)
        .then((rows) => {
          rows.forEach((row) => {
            const target = byName.get(row.name);
            if (target) Object.assign(target, { ...row, name: target.name });
          });
        })
        .catch(() => {})
    )
  );
  return records;
}

export function loadSection(ctx, sectionId) {
  const spec = catalogFor(ctx.engine)?.sections?.[sectionId];
  if (!spec) return Promise.resolve([]);
  const e = entryFor(ctx);
  let p = e.sections.get(sectionId);
  if (!p) {
    p = runSpec(ctx, spec)
      .then((records) => withExtras(ctx, records, spec.extras))
      .catch((err) => {
        e.sections.delete(sectionId);
        throw err;
      });
    e.sections.set(sectionId, p);
  }
  return p;
}

/** Every table's columns + every foreign key in scope → the diagram's model. */
export function loadDiagram(ctx) {
  const spec = catalogFor(ctx.engine);
  if (!spec) return Promise.resolve(buildDiagramModel([], []));
  return cached(entryFor(ctx), 'diagram', async () => {
    const [cols, fks] = await Promise.all([runSpec(ctx, spec.diagramColumns), runSpec(ctx, spec.diagramForeignKeys)]);
    return buildDiagramModel(cols, fks);
  });
}

// A tree Refresh, a reconnect or a disconnect makes what was read stale.
onSchemaCacheInvalidated(invalidateOverviewConnection);

export const __resetOverviewCache = () => cache.clear();
